import { extensions } from "@repo/fika-blog/src/extensions"
import { renderToText } from "@repo/fika-blog/src/lib/render-to-text"
import { assignInlineVars } from "@vanilla-extract/dynamic"
import { format } from "date-fns"
import { createMemo, For, Show } from "solid-js"
import { generateSearchHighlighter } from "@/data/search"
import { Box } from "@/designSystem/components/box"
import { Text } from "@/designSystem/components/text"
import {
  createFragment,
  type FragmentOf,
  graphql,
  readFragment,
} from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import {
  getReactionSummary,
  PostReactionSummaryFragment,
} from "@/lib/get-reaction-summary"
import { getTitle } from "@/lib/get-title"
import { useRelativeTime } from "@/lib/use-relative-time"
import { createBlogRouter } from "@/routes/posts-router"
import { Icon } from "../components/icon"
import { Mark } from "../components/mark"
import { ResponsiveImage } from "../components/responsive-image"
import { type Pill, Pills } from "./pills"
import {
  postClass,
  postStackClass,
  postStatClass,
  postStatsClass,
  postTitleClass,
  reactionCountClass,
  reactionEmojiClass,
  reactionsClass,
  stackRot1,
  stackRot2,
  stackX1,
  stackX2,
  stackY1,
  stackY2,
} from "./post.css"
import { PostStatusPillFragment, postStatusPill } from "./post-status-pill"
import { Quote } from "./quote"

export const PostFragment = graphql(
  `fragment Post on PostSearch {
    id
    rank
    highlight {
      content
      description
      title
    }
    post {
      id
      private
      tags {
        id
        tags
      }
      createdAt
      firstPublishedAt
      publishedAt
      subscriberCount
      ...PostStatusPillFragment
      stat {
        id
        recentViews
        openRatePercent
      }
      ...PostReactionSummary
      currentVersion {
        id
        title
        description
        content
        imageHeaderUrl
      }
    }
  }`,
  [PostReactionSummaryFragment, PostStatusPillFragment],
)

type Props = {
  postSearch: FragmentOf<typeof PostFragment>
}

export function PostComponent(props: Props) {
  const getPostSearch = createFragment(PostFragment, () => props.postSearch)
  const getPost = createMemo(() => getPostSearch().post)
  const title = createMemo(() => getTitle(getPost().currentVersion?.title))
  const searchResult = createMemo(() => ({
    rank: getPostSearch().rank,
    highlight: getPostSearch().highlight,
  }))

  const hl = createMemo(() => generateSearchHighlighter(searchResult()))
  const { LL } = useI18nContext()
  const { paths } = createBlogRouter()

  const stackStyles = createMemo(() => {
    const random = (min: number, max: number) =>
      min + Math.random() * (max - min)
    return assignInlineVars({
      [stackX1]: `${random(5, 8)}px`,
      [stackY1]: `${random(5, 8)}px`,
      [stackRot1]: `${random(0.5, 1)}deg`,
      [stackX2]: `${random(5, 8)}px`,
      [stackY2]: `${random(5, 8)}px`,
      [stackRot2]: `${-random(0.5, 1)}deg`,
    })
  })

  const titleHl = createMemo(() => hl()("title", title()))
  const truncatedBody = createMemo(() => {
    const json = getPost().currentVersion?.content
    if (!json) return ""

    try {
      return renderToText({
        extensions: extensions({ dropcursor: false }),
        doc: JSON.parse(json),
      }).slice(0, 300)
    } catch (e) {
      console.error(e)
      return ""
    }
  })

  const contentHl = createMemo(() => hl()("content"))
  const postDate = createMemo(
    () => getPost().firstPublishedAt || getPost().createdAt,
  )
  const relativeTime = useRelativeTime(() => postDate() ?? new Date())
  const humanReadableDate = createMemo(() => {
    const date = postDate()
    if (!date) return null
    const verb = getPost().publishedAt
      ? LL().dashboard.published()
      : LL().dashboard.created()

    return `${verb} ${relativeTime()}`
  })
  const formatDate = createMemo(() => {
    const date = postDate()
    if (!date) return undefined

    return format(date, "PPp")
  })
  const postStat = createMemo(() => {
    const stat = getPost().stat
    return stat
      ? { recentViews: stat.recentViews, openRatePercent: stat.openRatePercent }
      : null
  })
  const subscriberCount = createMemo(() => getPost().subscriberCount ?? 0)
  const getTags = createMemo(() => getPost().tags?.tags ?? [])

  const reactions = createMemo(() =>
    getReactionSummary(
      readFragment(PostReactionSummaryFragment, getPost()).reactions,
    ),
  )

  const pills = createMemo(() => {
    const pills: Pill[] = [postStatusPill(getPost())]

    if (getPost().private) {
      pills.push({
        icon: "lock",
        label: LL().common.private(),
        hue: "amber",
      })
    }

    for (const tag of getTags()) {
      pills.push({
        label: tag,
        rounded: true,
        hue: "sand",
      })
    }

    return pills
  })

  return (
    <div class={postStackClass} style={stackStyles()}>
      <a
        class={postClass}
        href={
          paths().post({
            id: getPost().id,
            title: getPost().currentVersion?.title ?? null,
          }).url
        }
      >
        <Box gap="16" fill>
          <Box gap="24" flexDirection="row">
            <ResponsiveImage
              image={{
                url: getPost().currentVersion?.imageHeaderUrl,
                imageClass: "smallThumb",
              }}
              srcset={[
                {
                  url: getPost().currentVersion?.imageHeaderUrl,
                  imageClass: "logo",
                  breakpoint: "xs",
                },
              ]}
              alt={getTitle(getPost().currentVersion?.title)}
            />
            <span class={postTitleClass}>
              <Mark hue="amber" pieces={titleHl()} />
            </span>
          </Box>
          <Text fontSize="s" color="sand900" lineClamp="3">
            <Text fontSize="s" color="sand700">
              <time title={formatDate()} datetime={formatDate()}>
                {humanReadableDate()}
                {truncatedBody().length > 0 ? " — " : ""}
              </time>
            </Text>
            {truncatedBody()}
          </Text>
          <Quote pieces={contentHl()} hue="amber" />
          <Pills pills={pills()} />
          <div class={postStatsClass}>
            <Show when={reactions().totalCount > 0}>
              <span class={reactionsClass}>
                <For each={reactions().topEmojis}>
                  {emoji => <span class={reactionEmojiClass}>{emoji}</span>}
                </For>
                <span class={reactionCountClass}>
                  {LL().common.countReactions(reactions().totalCount)}
                </span>
              </span>
            </Show>
            <div class={postStatClass}>
              <Icon name="eye" color="sand500" size="18" />
              {LL().common.countViews(postStat()?.recentViews ?? 0)}
            </div>
            <Show when={postStat()?.openRatePercent != null}>
              <div class={postStatClass}>
                <Icon name="mail" color="sand500" size="18" />
                {`${postStat()!.openRatePercent!}%`}
              </div>
            </Show>
            <div class={postStatClass}>
              <Icon name="users-round" color="sand500" size="18" />
              {subscriberCount()} {LL().dashboard.subscribers()}
            </div>
          </div>
        </Box>
      </a>
    </div>
  )
}
